import type Keycloak from 'keycloak-js';

import { clientDebugLog } from './clientDebugLog';
import { getKeycloak } from './keycloak';
import { getKeycloakInitOptions } from './keycloakOptions';
import {
	formatKeycloakOAuthError,
	parseKeycloakOAuthError,
	saveLastKeycloakOAuthError,
	type KeycloakOAuthError,
} from './keycloakOAuthError';
import {
	clearOAuthCallbackFromUrl,
	hasOAuthCallbackInCurrentUrl,
} from './keycloakRedirect';
import {
	applyPersistedTokensToKeycloak,
	persistKeycloakTokens,
} from './keycloakTokenRefresh';
import { clearKeycloakInMemorySession } from './keycloakLogout';
import {
	clearPersistedKeycloakSession,
	getRefreshTokenDebugMeta,
	isRefreshTokenUsable,
	loadPersistedKeycloakSession,
	type PersistedKeycloakSession,
	preloadPersistedKeycloakSession,
} from './keycloakTokenStorage';

/** Result of a Keycloak `init` attempt, including the access token when authenticated. */
export type InitKeycloakResult = {
	authenticated: boolean;
	token?: string;
	oauthError?: KeycloakOAuthError;
};

/**
 * Applies persisted tokens and validates them with Keycloak before treating the user as signed in.
 */
async function tryRestorePersistedSession(
	keycloak: Keycloak,
	persisted: PersistedKeycloakSession,
): Promise<boolean> {
	if (!isRefreshTokenUsable(persisted)) {
		clientDebugLog('info', '[auth:init] persisted refresh token unusable', {
			savedAt: persisted.savedAt,
			...getRefreshTokenDebugMeta(persisted.refreshToken),
		});
		return false;
	}

	applyPersistedTokensToKeycloak(keycloak, persisted);

	try {
		await keycloak.updateToken(-1);
		persistKeycloakTokens(keycloak);

		const ok = Boolean(keycloak.token);
		clientDebugLog('info', '[auth:init] persisted session restore', {
			restored: ok,
			...getRefreshTokenDebugMeta(keycloak.refreshToken ?? persisted.refreshToken),
		});
		return ok;
	} catch (error) {
		const oauthError = await parseKeycloakOAuthError(error);
		clientDebugLog('warn', '[auth:init] persisted session restore failed', {
			error: oauthError?.error,
			errorDescription: oauthError?.error_description,
			message: error instanceof Error ? error.message : String(error),
			savedAt: persisted.savedAt,
			...getRefreshTokenDebugMeta(persisted.refreshToken),
		});
		clearKeycloakInMemorySession(keycloak);
		clearPersistedKeycloakSession();

		return false;
	}
}

/**
 * Initializes Keycloak with PKCE, restores a persisted session when possible,
 * and strips OAuth callback parameters from the current URL when present.
 */
export async function initKeycloak(): Promise<InitKeycloakResult> {
	const keycloak = getKeycloak();
	const hadOAuthCallback = hasOAuthCallbackInCurrentUrl();

	await preloadPersistedKeycloakSession();

	if (hadOAuthCallback) {
		clearPersistedKeycloakSession();
	}

	const persisted = hadOAuthCallback ? null : loadPersistedKeycloakSession();

	clientDebugLog('info', '[auth:init] starting', {
		hadOAuthCallback,
		hasPersistedSession: Boolean(persisted),
		...(persisted ? getRefreshTokenDebugMeta(persisted.refreshToken) : {}),
	});

	try {
		const authenticated = await keycloak.init(
			getKeycloakInitOptions({ skipPersistedTokens: hadOAuthCallback }),
		);

		if (authenticated && keycloak.token) {
			persistKeycloakTokens(keycloak);
			clientDebugLog('info', '[auth:init] authenticated via keycloak.init', {
				hadOAuthCallback,
				...getRefreshTokenDebugMeta(keycloak.refreshToken),
			});

			return { authenticated: true, token: keycloak.token };
		}

		if (
			persisted &&
			(await tryRestorePersistedSession(keycloak, persisted))
		) {
			return {
				authenticated: true,
				token: keycloak.token ?? persisted.token,
			};
		}

		clientDebugLog('info', '[auth:init] unauthenticated', {
			hadOAuthCallback,
			hadPersistedSession: Boolean(persisted),
		});

		return { authenticated: false };
	} catch (error) {
		const oauthError = await parseKeycloakOAuthError(error);

		if (oauthError) {
			clientDebugLog('warn', hadOAuthCallback
				? '[auth:init] OAuth callback failed'
				: '[auth:init] session restore failed', {
				error: oauthError.error,
				errorDescription: oauthError.error_description,
				formatted: formatKeycloakOAuthError(oauthError),
				hadOAuthCallback,
				hadPersistedSession: Boolean(persisted),
				...getRefreshTokenDebugMeta(keycloak.refreshToken ?? persisted?.refreshToken),
			});

			if (hadOAuthCallback) {
				saveLastKeycloakOAuthError(oauthError);
			}
		} else {
			clientDebugLog('warn', '[auth:init] keycloak.init failed', {
				message: error instanceof Error ? error.message : String(error),
				hadOAuthCallback,
				hadPersistedSession: Boolean(persisted),
				...getRefreshTokenDebugMeta(keycloak.refreshToken ?? persisted?.refreshToken),
			});
		}

		if (keycloak.authenticated && keycloak.token) {
			persistKeycloakTokens(keycloak);

			return { authenticated: true, token: keycloak.token };
		}

		if (hadOAuthCallback) {
			clearPersistedKeycloakSession();

			return { authenticated: false, oauthError };
		}

		if (
			persisted &&
			(await tryRestorePersistedSession(keycloak, persisted))
		) {
			return {
				authenticated: true,
				token: keycloak.token ?? persisted.token,
			};
		}

		clearPersistedKeycloakSession();

		return { authenticated: false };
	} finally {
		if (hadOAuthCallback) {
			clearOAuthCallbackFromUrl();
		}
	}
}

export { getKeycloak };
